/* Thin-page rule helpers for the console.

   The rule itself lives in the backend (backend/seo_thin.py, a copy of the
   sites repo's module); the console never re-derives a verdict, it only
   shows the {thin, reasons, exempt} the API computed and edits the domain's
   seo.thin_rules overrides. A thin page or listing gets
   <meta name="robots" content="noindex, follow"> and is left out of
   sitemap.xml until it is improved. */

export const RULE_FIELDS = [
  { group: 'pages', key: 'min_words', type: 'number', label: 'Minimum words (articles)',
    hint: 'Body words: introduction plus page sections.' },
  { group: 'pages', key: 'hub_min_words', type: 'number', label: 'Minimum words (hub pages)',
    hint: 'Pages with child pages need less text of their own.' },
  { group: 'pages', key: 'hub_min_children', type: 'number', label: 'Minimum published children (hub pages)',
    hint: 'A hub with fewer published children is thin.' },
  { group: 'pages', key: 'require_description', type: 'bool', label: 'Require a meta description' },
  { group: 'pages', key: 'require_main_image', type: 'bool', label: 'Require a main image' },
  { group: 'pages', key: 'exempt_page_types', type: 'list', label: 'Exempt page types',
    hint: 'Comma separated. Pages of these types are never thin.' },
  { group: 'listings', key: 'min_score', type: 'number', label: 'Minimum listing score',
    hint: 'Description 2, photo 1, hours 1, website/booking/Instagram 1, linked person 1. Claimed listings always pass.' },
  { group: 'listings', key: 'description_min_words', type: 'number', label: 'Words for a description to count' },
];

export const fieldId = (f) => `${f.group}.${f.key}`;

const sameValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/* Effective rules -> editable strings/booleans keyed "group.key". */
export function formFromRules(rules) {
  const form = {};
  RULE_FIELDS.forEach((f) => {
    const v = rules?.[f.group]?.[f.key];
    if (f.type === 'bool') form[fieldId(f)] = !!v;
    else if (f.type === 'list') form[fieldId(f)] = (v || []).join(', ');
    else form[fieldId(f)] = v == null ? '' : String(v);
  });
  return form;
}

export function parseTypes(text) {
  const out = [];
  String(text || '').split(',').forEach((s) => {
    const v = s.trim().toLowerCase();
    if (v && !out.includes(v)) out.push(v);
  });
  return out;
}

/* Form -> {rules, errors}. errors is keyed like the form. */
export function parseForm(form) {
  const rules = { pages: {}, listings: {} };
  const errors = {};
  RULE_FIELDS.forEach((f) => {
    const id = fieldId(f);
    const raw = form[id];
    if (f.type === 'bool') rules[f.group][f.key] = !!raw;
    else if (f.type === 'list') rules[f.group][f.key] = parseTypes(raw);
    else {
      const s = String(raw ?? '').trim();
      if (!/^\d+$/.test(s)) errors[id] = 'A whole number, 0 or more';
      else rules[f.group][f.key] = parseInt(s, 10);
    }
  });
  return { rules, errors };
}

/* Only the values that differ from the defaults, in the stored shape. A key
   equal to its default is left out, so it keeps following the default. */
export function overridesFor(rules, defaults) {
  const out = {};
  RULE_FIELDS.forEach((f) => {
    const v = rules?.[f.group]?.[f.key];
    if (v === undefined) return;
    if (!sameValue(v, defaults?.[f.group]?.[f.key])) {
      out[f.group] = { ...(out[f.group] || {}), [f.key]: v };
    }
  });
  return out;
}

/* What an editor can do about each reason the API gives. */
const FIXES = [
  [/words on this hub page/, 'Add an introduction to this hub: what the section covers and who it is for.'],
  [/^Only \d+ words/, 'Add body text, in the introduction or as page sections.'],
  [/published child pages/, 'Publish more pages under this hub, or move its children elsewhere and unmark it as a hub.'],
  [/^No meta description/, 'Write a meta description (SEO card).'],
  [/^No main image/, 'Choose a main image.'],
  [/still processing/, 'Save the page again once the image upload finishes, or pick the image again.'],
  [/^No description|^Description is/, 'Add a description of at least the required length.'],
  [/^No photos/, 'Add a photo.'],
  [/^No opening hours/, 'Add opening hours.'],
  [/^No website/, 'Add a website, booking link or Instagram.'],
  [/^No linked people/, 'Link a person who works here.'],
  [/^Score /, 'Each item below adds to the score, or the owner can claim the listing.'],
];

export function thinFix(reason) {
  const hit = FIXES.find(([re]) => re.test(reason || ''));
  return hit ? hit[1] : null;
}

/* Tooltip text for a badge. */
export function thinTitle(verdict) {
  if (!verdict) return '';
  if (verdict.exempt) return 'Exempt by page type: never marked thin';
  if (!verdict.thin) return 'Not thin';
  return 'Hidden from search engines and the sitemap: ' + (verdict.reasons || []).join('; ');
}

/* The verdict for a page row from a /pages/thin response. */
export function pageVerdict(thinMap, page) {
  if (!thinMap || !page) return null;
  return thinMap[page.slug] || thinMap[page.id] || null;
}

export function countThin(thinMap, pages) {
  if (!thinMap) return { thin: 0, total: 0 };
  const list = pages || Object.keys(thinMap).map((slug) => ({ slug }));
  let thin = 0;
  list.forEach((p) => { if (pageVerdict(thinMap, p)?.thin) thin += 1; });
  return { thin, total: list.length };
}

export function filterThin(pages, thinMap, thinOnly) {
  if (!thinOnly) return pages;
  return (pages || []).filter((p) => pageVerdict(thinMap, p)?.thin);
}
